import React, { useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { apiBaseUrl } from "../config";

import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import ChecklistIcon from "@mui/icons-material/Checklist";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import TableChartIcon from "@mui/icons-material/TableChart";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import TitleIcon from "@mui/icons-material/Title";
import ImageIcon from "@mui/icons-material/Image";

import Markdown from "./Markdown";
import { tokens } from "../theme/tokens";

export default function MarkdownEditor({
  value,
  onChange,
  label = "Description",
  minRows = 12,
  placeholder = "Write markdown here...",
  error = false,
  helperText = "",
  sx = {},
  enableImageUpload = false,
  tenantId,
  taskId,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [tab, setTab] = useState("write");
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageAltText, setImageAltText] = useState("");
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const canUploadImages = enableImageUpload && tenantId && taskId;

  const insertMarkdown = (before, after = "") => {
    const textarea = document.getElementById("markdown-editor-input");

    if (!textarea) {
      onChange(`${value || ""}${before}${after}`);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.slice(start, end);

    const nextValue =
      value.slice(0, start) +
      before +
      selectedText +
      after +
      value.slice(end);

    onChange(nextValue);

    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = start + before.length;
      textarea.selectionEnd = start + before.length + selectedText.length;
    }, 0);
  };

  const insertBlock = (text) => {
    const prefix = value?.trim() ? "\n\n" : "";
    onChange(`${value || ""}${prefix}${text}`);
  };

  const handleImageUpload = async () => {
    if (!selectedImage || !canUploadImages) return;

    setIsUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append("tenantId", tenantId);
      formData.append("sopId", taskId);
      formData.append("file", selectedImage);

      const response = await fetch(`${apiBaseUrl}/api/uploads/sop-image`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload image");
      }

      const data = await response.json();

      const imageUrl = data.url.startsWith("http")
      ? data.url
      : `${apiBaseUrl}${data.url}`;

      insertBlock(`![${imageAltText || selectedImage.name}](${imageUrl})`);

      setSelectedImage(null);
      setImageAltText("");
      setImageDialogOpen(false);
    } catch (err) {
      console.error("Image upload failed:", err);
    } finally {
      setIsUploadingImage(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: error ? theme.palette.error.main : colors.scalpelTeal[700],
        overflow: "hidden",
        backgroundColor: colors.background.paper,
        ...sx,
      }}
    >
      <Box
        sx={{
          borderBottom: `1px solid ${colors.surface.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          px: 1,
        }}
      >
        <Tabs value={tab} onChange={(_, nextTab) => setTab(nextTab)}>
          <Tab label="Write" value="write" />
          <Tab label="Preview" value="preview" />
        </Tabs>

        <ButtonGroup size="small" variant="text">
          {canUploadImages && (
            <Tooltip title="Upload image">
              <Button onClick={() => setImageDialogOpen(true)}>
                <ImageIcon fontSize="small" />
              </Button>
            </Tooltip>
          )}

          <Tooltip title="Heading">
            <Button onClick={() => insertBlock("## Heading")}>
              <TitleIcon fontSize="small" />
            </Button>
          </Tooltip>

          <Tooltip title="Bold">
            <Button onClick={() => insertMarkdown("**", "**")}>
              <FormatBoldIcon fontSize="small" />
            </Button>
          </Tooltip>

          <Tooltip title="Italic">
            <Button onClick={() => insertMarkdown("_", "_")}>
              <FormatItalicIcon fontSize="small" />
            </Button>
          </Tooltip>

          <Tooltip title="Bullet list">
            <Button onClick={() => insertBlock("- List item")}>
              <FormatListBulletedIcon fontSize="small" />
            </Button>
          </Tooltip>

          <Tooltip title="Numbered list">
            <Button onClick={() => insertBlock("1. List item")}>
              <FormatListNumberedIcon fontSize="small" />
            </Button>
          </Tooltip>

          <Tooltip title="Checklist">
            <Button onClick={() => insertBlock("- [ ] Checklist item")}>
              <ChecklistIcon fontSize="small" />
            </Button>
          </Tooltip>

          <Tooltip title="Quote">
            <Button onClick={() => insertBlock("> Important note")}>
              <FormatQuoteIcon fontSize="small" />
            </Button>
          </Tooltip>

          <Tooltip title="Divider">
            <Button onClick={() => insertBlock("---")}>
              <HorizontalRuleIcon fontSize="small" />
            </Button>
          </Tooltip>

          <Tooltip title="Table">
            <Button
              onClick={() =>
                insertBlock("| Column | Column |\n|---|---|\n| Value | Value |")
              }
            >
              <TableChartIcon fontSize="small" />
            </Button>
          </Tooltip>
        </ButtonGroup>
      </Box>

      {tab === "write" ? (
        <TextField
          id="markdown-editor-input"
          label={label}
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          multiline
          minRows={minRows}
          fullWidth
          variant="outlined"
          sx={{
            "& fieldset": { border: "none" },
            "& textarea": {
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              lineHeight: 1.7,
            },
          }}
        />
      ) : (
        <Box sx={{ p: 2, minHeight: 260 }}>
          {value?.trim() ? (
            <Markdown>{value}</Markdown>
          ) : (
            <Box sx={{ color: colors.text.secondary }}>
              Nothing to preview yet.
            </Box>
          )}
        </Box>
      )}

      <Divider />

      <Box
        sx={{
          px: 2,
          py: 1,
          fontSize: "0.8rem",
          color: error ? theme.palette.error.main : colors.text.secondary,
        }}
      >
        {helperText ||
          "Supports headings, lists, checklists, tables, quotes, dividers, images, bold and italic text."}
      </Box>

      {canUploadImages && (
        <Dialog
          open={imageDialogOpen}
          onClose={() => setImageDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Upload image</DialogTitle>

          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pt: 2,
            }}
          >
            <Button variant="outlined" component="label">
              Select image
              <input
                hidden
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={(event) => {
                  const file = event.target.files?.[0];

                  if (!file) return;

                  setSelectedImage(file);
                  setImageAltText(file.name.replace(/\.[^/.]+$/, ""));
                }}
              />
            </Button>

            {selectedImage && (
              <Box sx={{ fontSize: "0.9rem", color: colors.text.secondary }}>
                Selected: {selectedImage.name}
              </Box>
            )}

            <TextField
              label="Image description"
              value={imageAltText}
              onChange={(event) => setImageAltText(event.target.value)}
              helperText="This becomes the markdown image description."
              fullWidth
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setImageDialogOpen(false)}>Cancel</Button>

            <Button
              variant="contained"
              onClick={handleImageUpload}
              disabled={!selectedImage || isUploadingImage}
            >
              {isUploadingImage ? "Uploading..." : "Upload"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Paper>
  );
}